import React, { useId } from 'react';
import { Box } from '@mui/material';

/**
 * The PyMedic logo mark: a rounded blue hexagon holding a navy hexagon with a
 * white cross. Drawn as SVG rather than shipped as an image so it stays sharp
 * from the 34px sidebar tile up to the login panel, and sits on light or dark
 * grounds without a background box. `glow` adds the soft blue halo.
 */
const BrandMark = ({ size = 40, glow = false, sx }) => {
  // Gradient ids must be unique per instance: several marks share a page.
  const id = useId().replace(/:/g, '');
  const outer = `bm-outer-${id}`;
  const inner = `bm-inner-${id}`;
  const shine = `bm-shine-${id}`;

  return (
    <Box
      component="svg"
      viewBox="0 0 100 100"
      role="img"
      aria-label="PyMedic"
      sx={{
        width: size,
        height: size,
        flex: 'none',
        display: 'block',
        overflow: 'visible',
        filter: glow
          ? 'drop-shadow(0 0 10px rgba(56,189,248,0.75)) drop-shadow(0 4px 10px rgba(21,101,192,0.35))'
          : 'drop-shadow(0 3px 6px rgba(13,59,140,0.28))',
        ...sx,
      }}
    >
      <defs>
        <linearGradient id={outer} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#4FC3F7" />
          <stop offset="0.5" stopColor="#1E88E5" />
          <stop offset="1" stopColor="#1565C0" />
        </linearGradient>
        <linearGradient id={inner} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#123E8F" />
          <stop offset="1" stopColor="#0A2463" />
        </linearGradient>
        <linearGradient id={shine} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#fff" stopOpacity="0.55" />
          <stop offset="1" stopColor="#fff" stopOpacity="0" />
        </linearGradient>
      </defs>

      {/* Outer hexagon; the round join is what softens its corners. */}
      <polygon
        points="50,12 82.9,31 82.9,69 50,88 17.1,69 17.1,31"
        fill={`url(#${outer})`}
        stroke={`url(#${outer})`}
        strokeWidth="14"
        strokeLinejoin="round"
      />
      {/* Gloss across the upper half. */}
      <path
        d="M50 13 L82 31.5 L82 46 Q50 36 18 46 L18 31.5 Z"
        fill={`url(#${shine})`}
        strokeLinejoin="round"
      />
      {/* Light rim, then the navy inner hexagon. */}
      <polygon
        points="50,23 73.4,36.5 73.4,63.5 50,77 26.6,63.5 26.6,36.5"
        fill="#BFE6FF"
        stroke="#BFE6FF"
        strokeWidth="5"
        strokeLinejoin="round"
      />
      <polygon
        points="50,26 70.8,38 70.8,62 50,74 29.2,62 29.2,38"
        fill={`url(#${inner})`}
        stroke={`url(#${inner})`}
        strokeWidth="3"
        strokeLinejoin="round"
      />
      {/* White cross. */}
      <rect x="44" y="36" width="12" height="28" rx="3.5" fill="#fff" />
      <rect x="36" y="44" width="28" height="12" rx="3.5" fill="#fff" />
    </Box>
  );
};

export default BrandMark;
